import * as React from "react";
import { useCallback, useMemo } from "react";
import _ from "lodash";
import fp from "lodash/fp";
import { AgGridReact } from "ag-grid-react";
import {
    CellDoubleClickedEvent,
    ColDef,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    RowNode,
} from "ag-grid-community";
import { $, merge } from "glamor";
import { Schema } from "types/schema";
import { PurchaseOrder } from "@nuorder/client-cart-api/dist/types/modules/order/models";
import { Warehouse } from "@nuorder/client-cart-api/dist/types/modules/brand/models";
import DropDownMenu from "material-ui/DropDownMenu";
import MenuItem from "material-ui/MenuItem";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import lodashSortBy from "lodash/sortBy";
import { theme } from "styles/theme";
import { PinnedFooterRowRenderer } from "./grid-components/renderers/pinned-footer-renderer";
import { ItemRenderer } from "./grid-components/renderers/item-renderer";
import { GroupRenderer } from "./grid-components/renderers/group-renderer";
import { formatMessage } from "lib/globalize-util";
import "./helpers/theme.css";
import { getQuantityWithUnitsPerPack } from "helpers/legacy-cart/line-items/items-conversion-helper";
import { buildColDef, buildDeliveryWindowColDef, extractLineItemIdByRowNode, getSortedTotals } from "./helpers";
import { ProductRequiredDisplayFields } from "helpers/legacy-cart/line-items/view-definition-config-helper";
import { Editors } from "constants/product-collection";
import { IItem, IPricing, IProductItem, ITotal } from "./types";
import {
    FdmAttribute,
    IDateRange,
    IDeliveryRange,
    IGridEntry,
    IOrderData,
    LineItemAttributes,
    type IOrderCurrency,
} from "types/cart/shipment-review";
import type { IScope } from "reducers/scope";
import { IViewDefinition, SECTIONS } from "types/view-definitions";
import type { IProduct } from "types/product";
import { useBuildOrder } from "hooks/useBuildOrder";

export enum ColumnTypes {
    DeliveryWindow = "DeliveryWindow",
}

const getRowNodeId = (data) => {
    return data.item.id;
};

export enum SortOptions {
    Cart = "cart",
    Name = "name",
    StyleNumber = "style_number",
    Color = "color",
    Units = "units",
    Wholesale = "wholesale",
}

const GRID_COMPONENTS = {
    [Editors.PinnedFooterRowRenderer]: PinnedFooterRowRenderer,
    [Editors.GroupRenderer]: GroupRenderer,
    [Editors.ItemRenderer]: ItemRenderer,
};

const columnTypes = {
    [ColumnTypes.DeliveryWindow]: {},
};

const styles = merge(
    {
        height: "100%",
        fontFamily: "Lato !important",
        padding: "0 24px 24px 24px",
    },
    $(" .grid", {
        width: "100%",
        height: "calc(100% - 52px)",
        margin: "0",
    }),
    $(" .grid .ag-root-wrapper-body", {
        height: "auto",
    }),
    $(" .ag-floating-bottom", {
        backgroundColor: `${theme.palette.lightGrey} !important`,
        height: "70px !important",
        minHeight: "70px !important",
    }),
    $(" .ag-pinned-right-floating-bottom > div > div", {
        backgroundColor: theme.palette.darkGrey,
    }),
    $(" .ag-pinned-left-floating-bottom > div, .ag-floating-bottom-container > div", {
        backgroundColor: `${theme.palette.lightGrey} !important`,
    }),
    $(" .ag-pinned-right-cols-container > div > div", {
        display: "flex",
        flexDirection: "row-reverse",
        alignItems: "flex-end",
        backgroundColor: "#f6f6f6 !important",
    }),
    $(" .ag-pinned-right-floating-bottom > div > div > div > div.pinnedCell > div.quantity", {
        color: `${theme.palette.white} !important`,
        height: "22px !important",
        lineHeight: "1.83 !important",
    }),
    $(" .ag-pinned-right-floating-bottom > div > div > div > div.pinnedCell > div.totalPrice", {
        color: `${theme.palette.white} !important`,
        fontSize: "14px !important",
        lineHeight: "1.57 !important",
        height: "22px !important",
        letterSpacing: "0.19px",
    }),
    $(" .ag-pinned-right-floating-bottom > div > div > div > div.pinnedCell > div.totalRetail", {
        color: `${theme.palette.white} !important`,
        lineHeight: "1.78 !important",
        marginBottom: "4px !important",
    }),
    $(" .ag-status-bar", {
        display: "none",
    }),
    $(" .ag-header-cell-label", {
        fontSize: "12px",
        fontWeight: "bold",
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: "1.25",
        letterSpacing: "0.16px",
        textAlign: "right",
        color: theme.palette.darkGrey,
        overflowWrap: "break-word",
        textAlignLast: "center",
    }),
    $(" .ag-header-cell-label > span", {
        whiteSpace: "normal !important",
    }),

    $(" .ag-center-cols-container > div > div", {
        display: "flex",
        flexDirection: "row-reverse",
        alignItems: "flex-end",
    }),
    $(" .ag-floating-bottom-container > div > div", {
        display: "flex  !important",
        flexDirection: "row-reverse  !important",
        alignItems: "flex-end  !important",
    }),
    $(" .ag-floating-bottom-container > div", {
        height: "70px !important",
        backgroundColor: "inherit !important",
    }),
    $(" .ag-pinned-left-floating-bottom > div > div", {
        display: "flex  !important",
        flexDirection: "row-reverse  !important",
        alignItems: "flex-end  !important",
    }),
    $(" .ag-pinned-left-floating-bottom > div", {
        height: "70px !important",
        backgroundColor: "inherit !important",
    }),
    $(" .ag-pinned-right-floating-bottom > div > div", {
        display: "flex  !important",
        flexDirection: "row-reverse  !important",
        alignItems: "flex-end  !important",
    }),
    $(" .ag-pinned-right-floating-bottom > div", {
        height: "70px !important",
        backgroundColor: "inherit",
    }),
    $(" .ag-center-cols-container > div > div.cell-disabled", {
        display: "flex",
        flexDirection: "column-reverse",
        backgroundColor: "#f6f6f6 !important",
    }),
);

const commonColDefProps = {
    lockPosition: true,
    suppressMovable: true,
    floatingFilter: false,
    suppressMenu: true,
    resizable: true,
    filter: false,
    sortable: false,
    enableRowGroup: true,
    enableValue: true,
};

interface IProps {
    orderData: IOrderData;
    productSchema: Schema;
    itemView: IViewDefinition;
    openSizingGrid: (items: IProductItem, refresh?: boolean) => void;
    viewLineItem: (lineItemId: string) => void;
    editable?: boolean;
    scope: IScope;
}

export const calculateTotals = (entry: IGridEntry, totals: IPricing): IPricing => {
    const quantity = entry.quantity;
    const wholeSalePrice = entry.pricing?.wholesale ?? 0;
    const discountedPrice = entry.pricing?.discounted_price;
    const originalWholesalePrice = entry.pricing?.adjusted_wholesale ?? entry.pricing?.original_wholesale ?? 0;
    const retailPrice = entry.pricing?.retail ?? 0;
    const unitPerPack = entry.unitsPerPack;

    const totalWholesalePrice = quantity * (discountedPrice ?? wholeSalePrice);
    const totalOriginalWholesalePrice = quantity * originalWholesalePrice;
    const totalRetailPrice = quantity * retailPrice;

    totals.quantity += getQuantityWithUnitsPerPack(quantity, unitPerPack);
    totals.totalRetailPrice += getQuantityWithUnitsPerPack(totalRetailPrice, unitPerPack);
    totals.totalWholesalePrice += getQuantityWithUnitsPerPack(totalWholesalePrice, unitPerPack);
    totals.totalOriginalWholesalePrice += getQuantityWithUnitsPerPack(totalOriginalWholesalePrice, unitPerPack);
    totals.currency = entry.pricing.currency;
    return totals;
};

// Deprecated, use calculateFooterTotalsByEntry instead
export const calculateFooterTotals = (
    target: IPricing,
    productTotals: IPricing,
    {
        quantity,
        wholeSalePrice,
        originalWholesalePrice,
        retailPrice,
        discountedPrice,
        currency,
    }: {
        quantity: number;
        wholeSalePrice: number;
        retailPrice: number;
        discountedPrice?: number;
        originalWholesalePrice?: number;
        currency?: IOrderCurrency;
    },
    unitPerPack?: number,
): { target: IPricing; productTotals: IPricing } => {
    const totals: any = { target, productTotals };
    const totalWholesalePrice = quantity * (discountedPrice ?? wholeSalePrice);
    const totalOriginalWholesalePrice = quantity * originalWholesalePrice;
    const totalRetailPrice = quantity * retailPrice;

    totals.target.quantity += getQuantityWithUnitsPerPack(quantity, unitPerPack);
    totals.target.totalRetailPrice += getQuantityWithUnitsPerPack(totalRetailPrice, unitPerPack);
    totals.target.totalWholesalePrice += getQuantityWithUnitsPerPack(totalWholesalePrice, unitPerPack);
    totals.target.totalOriginalWholesalePrice += getQuantityWithUnitsPerPack(totalOriginalWholesalePrice, unitPerPack);
    totals.target.currency = currency;

    totals.productTotals.quantity += getQuantityWithUnitsPerPack(quantity, unitPerPack);
    totals.productTotals.totalRetailPrice += getQuantityWithUnitsPerPack(totalRetailPrice, unitPerPack);
    totals.productTotals.totalWholesalePrice += getQuantityWithUnitsPerPack(totalWholesalePrice, unitPerPack);
    totals.productTotals.totalOriginalWholesalePrice += getQuantityWithUnitsPerPack(
        totalOriginalWholesalePrice,
        unitPerPack,
    );
    totals.productTotals.currency = currency;

    return totals;
};

export const sortPurchaseOrdersByDeliveryDates = (purchaseOrders: PurchaseOrder[]) => {
    return purchaseOrders.sort((a, b) => {
        return a.shipStart.getTime() - b.shipStart.getTime() + a.shipEnd.getTime() - b.shipEnd.getTime();
    });
};

export const sortHandler = (rowData: any[], asc: boolean, property: (o: any) => any) => {
    const auxRowData = rowData.map((item, index) => ({ ...item, index }));
    const sortedData = lodashSortBy(auxRowData, [property]);
    if (!asc) {
        sortedData.reverse();
    }
    return sortedData;
};

export const getDateKey = (dateStart: Date | undefined, dateEnd: Date | undefined): string => {
    const format = (date) => (date ? `${date.getUTCDate()}_${date.getUTCMonth()}_${date.getUTCFullYear()}` : "");
    return `${format(dateStart)}_${format(dateEnd)}`;
};

export const getUniqDateRanges = (list: IDeliveryRange[]): IDateRange[] =>
    fp.compose(
        fp.sortBy(["shipStart", "shipEnd"]),
        fp.uniqBy("dateKey"),
        fp.map(({ shipStart, shipEnd }: IDeliveryRange) => ({
            shipStart,
            shipEnd,
            dateKey: getDateKey(shipStart, shipEnd),
        })),
    )(list);

export const isSameRange = fp.curry(
    (a: IDeliveryRange, b: IDeliveryRange) =>
        a === b || (+a?.shipStart === +b?.shipStart && +a?.shipEnd === +b?.shipEnd),
);

export const buildKeyFields = (
    productSchema: Schema,
    product: IProduct,
    prebook: boolean,
    warehouse: Warehouse | null,
    fdmAttributes?: LineItemAttributes,
) => {
    const keyFields = {} as any;
    productSchema?.fields?.forEach(({ key }) => {
        keyFields[key] = product[key];
    });
    keyFields.prebook = prebook;
    if (warehouse) {
        keyFields.warehouse = warehouse.id;
    }

    if (fdmAttributes) {
        const keys = Object.keys(fdmAttributes);
        keys.forEach((key) => {
            keyFields[key] = fdmAttributes[key].value;
        });
    }

    return keyFields;
};

export const buildCompositedKeyFields = (
    productSchema: Schema,
    product: IProduct,
    prebook: boolean,
    warehouse: Warehouse | null,
    fdmAttributes?: { [field: string]: FdmAttribute },
) => {
    const compositeKeyFields = {} as any;
    productSchema?.composite_key_fields?.forEach((key) => {
        compositeKeyFields[key] = product[key];
    });
    compositeKeyFields.prebook = prebook;
    if (warehouse) {
        compositeKeyFields.warehouse = warehouse.id;
    }

    if (fdmAttributes) {
        const keys = Object.keys(fdmAttributes);
        keys.forEach((key) => {
            compositeKeyFields[key] = fdmAttributes[key].value;
        });
    }

    return compositeKeyFields;
};

// TODO: use IGridEntry instead
export const generateShipmentReviewRowData = (
    orderData: IOrderData | null,
    itemView: IViewDefinition,
    productSchema: Schema,
): IItem[] => {
    const initialPricing = {};
    _.forEach(
        orderData?.uniqueDates,
        (dateObject) =>
            (initialPricing[dateObject.dateKey] = {
                currency: orderData?.currency,
                quantity: 0,
                wholesalePrice: {},
                retailPrice: {},
                unitsPerPack: 1,
            }),
    );

    return _.map(orderData.entries, (entry: IGridEntry) => {
        const pricing = { ...initialPricing };
        pricing[getDateKey(entry.shipStart, entry.shipEnd)] = {
            quantity: entry?.quantity,
            currency: orderData?.currency,
            wholesalePrice: {
                currency: orderData?.currency.wholesale.code,
                amount: entry.pricing?.discounted_price ?? entry.pricing?.wholesale ?? 0,
            },
            unitsPerPack: entry?.unitsPerPack || entry.size.__units_per_pack || 1,
            // TODO: need to replace 'orderData?.currency.symbol' with currency code.
            retailPrice: { currency: orderData?.currency.retail.code, amount: entry.pricing?.retail },
        };
        return {
            item: entry,
            shipStart: entry.shipStart,
            shipEnd: entry.shipEnd,
            itemView,
            pricing,
            orderDiscount: entry.pricing.discount,
            productSchema: buildKeyFields(
                productSchema,
                entry?.product,
                entry?.prebook ?? false,
                entry?.warehouse,
                entry?.attributes,
            ),
            compositeKeyFields: buildCompositedKeyFields(
                productSchema,
                entry?.product,
                entry?.prebook ?? false,
                entry?.warehouse,
                entry?.attributes,
            ),
            currency: orderData.currency,
            enforceAvailableUntil: orderData?.deliveryConfig?.enforceAvailableUntil,
            minimumDeliveryWindow: orderData?.deliveryConfig?.minimumDeliveryWindow,
            defaultDeliveryWindow: orderData?.deliveryConfig?.defaultDeliveryWindow,
            cumulativeDate: orderData?.deliveryConfig?.cumulativeDate,
        };
    });
};

export const ShipmentReviewGrid: React.FC<IProps> = ({
    orderData,
    productSchema,
    itemView,
    viewLineItem,
    openSizingGrid,
    editable,
    scope,
}) => {
    const { isBuildOrderEnabled } = useBuildOrder();
    const columnDefs = React.useMemo<ColDef[]>(() => {
        const colDefs: ColDef[] = [];

        orderData?.uniqueDates.forEach((dateObject) => {
            colDefs.push(buildDeliveryWindowColDef(dateObject.shipStart, dateObject.shipEnd, { isBuildOrderEnabled }));
        });

        productSchema?.fields?.forEach((item) => {
            colDefs.push(buildColDef(item));
        });

        colDefs.push({
            headerName: formatMessage("Size"),
            colId: "size",
            hide: true,
            valueGetter: ({ data }): any => {
                if (!data) {
                    return "";
                }
                const rowData = data as IItem;
                return rowData?.item?.size?.size;
            },
        });

        colDefs.push({
            headerName: formatMessage("UPC"),
            colId: "upc",
            hide: true,
            valueGetter: ({ data }): any => {
                if (!data) {
                    return "";
                }
                const rowData = data as IItem;
                return rowData?.item?.product?.upc;
            },
        });

        colDefs.push({
            headerName: "Composite Key Fields",
            colId: "composite_key_fields",
            hide: true,
            rowGroup: true,
            width: 263,
            valueGetter: ({ data }): any => {
                if (!data) {
                    return "";
                }
                const rowData = data as IItem;
                return JSON.stringify(rowData?.compositeKeyFields);
            },
        });

        colDefs.push({
            headerName: formatMessage("Product Totals"),
            colId: "total",
            cellRendererSelector: (params: ICellRendererParams) => {
                if (params.node.rowPinned === "bottom") {
                    return {
                        component: Editors.PinnedFooterRowRenderer,
                    };
                }
                return {
                    component: Editors.ItemRenderer,
                };
            },
            pinned: "right",
            width: 122,
        });

        return colDefs;
    }, [isBuildOrderEnabled, orderData?.uniqueDates, productSchema?.fields]);

    const autoGroupColumnDef: ColDef = React.useMemo(
        () => ({
            headerName: "",
            pinned: "left",
            width: 263,
            autoHeight: true,
            cellRendererSelector: (params: ICellRendererParams) => {
                if (params.node.rowPinned === "bottom") {
                    return {
                        component: Editors.PinnedFooterRowRenderer,
                    };
                }
                return {
                    component: Editors.GroupRenderer,
                    params: { brandId: scope.brand?._id },
                };
            },
        }),
        [scope.brand?._id],
    );

    const rowData = React.useMemo<IItem[]>(
        () => generateShipmentReviewRowData(orderData, itemView, productSchema),
        [orderData, itemView, productSchema],
    );

    const [lastOpenedNodeId, setLastOpenedNodeId] = React.useState<string>();
    const [gridApi, setGridApi] = React.useState<GridApi | null>(null);

    const handleOnGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);
    };

    const calculatePinnedBottomData = () => {
        const target: ITotal = {
            quantity: 0,
            totalRetailPrice: 0,
            totalWholesalePrice: 0,
            total: { quantity: 0, totalRetailPrice: 0, totalWholesalePrice: 0 },
        };
        const columnsWithAggregation = orderData?.uniqueDates;

        columnsWithAggregation?.forEach((element) => {
            target[element.dateKey] = {
                quantity: 0,
                totalRetailPrice: 0,
                totalWholesalePrice: 0,
                currency: orderData.currency,
            };
        });

        columnsWithAggregation?.forEach((element) => {
            gridApi.forEachNodeAfterFilter(({ data }) => {
                if (data?.pricing[element.dateKey]?.quantity > 0) {
                    const unitsPerPack = data.pricing[element.dateKey].unitsPerPack ?? 1;
                    const pricing = {
                        quantity: data.pricing[element.dateKey].quantity,
                        wholeSalePrice: data.pricing[element.dateKey].wholesalePrice.amount ?? 0,
                        retailPrice: data.pricing[element.dateKey].retailPrice.amount ?? 0,
                        currency: data.pricing.currency,
                        unitsPerPack,
                    };

                    const { target: currentElement, productTotals } = calculateFooterTotals(
                        { ...target[element.dateKey] },
                        { ...target.total },
                        pricing,
                        unitsPerPack,
                    );
                    target[element.dateKey] = currentElement;
                    target.total = productTotals;
                }
            });
        });
        return target;
    };

    React.useEffect(() => {
        if (!gridApi || !rowData) {
            return;
        }

        gridApi.setRowData(rowData);
        const pinnedBottomData = calculatePinnedBottomData();
        gridApi.setPinnedBottomRowData([pinnedBottomData]);
        // gridApi.refreshCells({ force: true, suppressFlash: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData, gridApi]);

    React.useEffect(() => {
        // Refresh modal outside iframe with latest data
        if (lastOpenedNodeId && editable) {
            gridApi?.forEachNode((rowNode) => {
                if (rowNode.group && rowNode.id === lastOpenedNodeId) {
                    openSizingGrid(collectDataForLineItemModal(rowNode));
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData, lastOpenedNodeId, openSizingGrid, editable, gridApi]);

    const [sortOptionSelected, setSortOptionSelected] = React.useState({
        current: SortOptions.Cart,
        asc: false,
    });

    const changeSortOption = (option: any): boolean => {
        let isAsc = null;
        setSortOptionSelected((prevState) => {
            isAsc = prevState.current === option ? !prevState.asc : true;
            return {
                current: option,
                asc: isAsc,
            };
        });
        return isAsc;
    };

    const getLeftIcon = useCallback(
        (value: string) => {
            return sortOptionSelected.current === value ? (
                sortOptionSelected.asc ? (
                    <KeyboardArrowUpIcon color="inherit" />
                ) : (
                    <KeyboardArrowDownIcon color="inherit" />
                )
            ) : null;
        },
        [sortOptionSelected],
    );

    const sortOptions = useMemo(() => {
        // TODO GV: weird logic to clean up
        const canDisplay = (reference) => _.some(itemView?.sections?.[SECTIONS.DETAILS]?.elements, { reference });
        const options = [
            {
                key: SortOptions.Cart,
                text: formatMessage("Added to cart"),
                leftIcon: getLeftIcon(SortOptions.Cart),
                property: (o) => o.index,
            },
            {
                key: SortOptions.Name,
                text: formatMessage("Name"),
                leftIcon: getLeftIcon(SortOptions.Name),
                property: (o) => o.productSchema.name,
                hide: !canDisplay(ProductRequiredDisplayFields.NAME),
            },
            {
                key: SortOptions.StyleNumber,
                text: formatMessage("Style Number"),
                leftIcon: getLeftIcon(SortOptions.StyleNumber),
                property: (o) => o.productSchema.style_number,
                hide: !canDisplay(ProductRequiredDisplayFields.STYLE_NUMBER),
            },
            {
                key: SortOptions.Color,
                text: formatMessage("Color"),
                leftIcon: getLeftIcon(SortOptions.Color),
                property: (o) => o.productSchema.color,
                hide: !canDisplay(ProductRequiredDisplayFields.COLOR),
            },
            {
                key: SortOptions.Units,
                text: formatMessage("Total Units"),
                leftIcon: getLeftIcon(SortOptions.Units),
                property: (o) => o.total.units,
            },
            {
                key: SortOptions.Wholesale,
                text: formatMessage("Total Wholesale"),
                leftIcon: getLeftIcon(SortOptions.Wholesale),
                property: (o) => o.total.wholesale,
            },
        ];

        return options.filter(({ hide }) => !hide);
    }, [getLeftIcon, itemView]);

    const handleChangeSort = (_, index, value) => {
        const asc = changeSortOption(value);
        const sortedData =
            value === SortOptions.Wholesale || value === SortOptions.Units
                ? getSortedTotals(rowData, asc, sortOptions[index].property)
                : sortHandler(rowData, asc, sortOptions[index].property);
        // No other way to make gridApi to re-render rows in appropriate order
        gridApi.setRowData([]);
        gridApi.setRowData(sortedData);
    };

    const handleCellClicked = React.useCallback(
        (e: CellDoubleClickedEvent) => {
            if (e.colDef.type === ColumnTypes.DeliveryWindow) {
                if (editable) {
                    if (e.rowPinned !== "bottom") {
                        setLastOpenedNodeId(e.node.id);
                        openSizingGrid(collectDataForLineItemModal(e.node, e.column.getColId()), true);
                    }
                } else {
                    const lineItemId = extractLineItemIdByRowNode(e.colDef, e.node);
                    if (!lineItemId) {
                        return;
                    }

                    viewLineItem(lineItemId);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [openSizingGrid, viewLineItem, editable],
    );

    const collectDataForLineItemModal = (node: RowNode, colId?: string): IProductItem => ({
        colId,
        items: _.map(node.allLeafChildren, "data"),
        uniqueDates: orderData?.uniqueDates,
    });

    return (
        <div id="shipmentReviewContainer" {...styles}>
            <div style={{ display: "flex", flexDirection: "column" }}>
                <DropDownMenu
                    value={sortOptionSelected.current}
                    onChange={handleChangeSort}
                    style={{ marginLeft: "auto", marginBottom: "20px", height: "32px" }}
                    labelStyle={{
                        color: theme.palette.darkGrey,
                        fontFamily: "Lato",
                        fontSize: "14px",
                        paddingRight: "35px",
                        fontWeight: "normal",
                        fontStretch: "normal",
                        fontStyle: "normal",
                        letterSpacing: "0.18px",
                    }}
                    iconButton={<KeyboardArrowDownIcon />}
                    iconStyle={{ fill: theme.palette.darkGrey, padding: "0", width: "auto", right: "3px" }}
                    underlineStyle={{ borderTop: `1px solid ${theme.palette.grey}`, margin: "-12px 0" }}
                    disabled={false}
                >
                    {sortOptions.map((item) => (
                        <MenuItem
                            label={`${formatMessage("Sort")}: ${item.text}`}
                            key={item.key}
                            value={item.key}
                            primaryText={item.text}
                            leftIcon={item.leftIcon}
                            style={{ backgroundColor: "transparent", marginLeft: item?.leftIcon ? "0" : "40px" }}
                        />
                    ))}
                </DropDownMenu>
            </div>
            <div className={`${theme.agGrid.defaultTheme} grid`}>
                <AgGridReact
                    onGridReady={handleOnGridReady}
                    defaultColDef={commonColDefProps}
                    columnDefs={rowData.length ? columnDefs : []}
                    columnTypes={columnTypes}
                    rowData={rowData}
                    rowDragManaged
                    debug={false}
                    frameworkComponents={GRID_COMPONENTS}
                    enableRangeSelection
                    enableCellChangeFlash
                    animateRows
                    suppressCopyRowsToClipboard
                    stopEditingWhenCellsLoseFocus
                    getRowNodeId={getRowNodeId}
                    enterMovesDownAfterEdit
                    sideBar={false}
                    autoGroupColumnDef={autoGroupColumnDef}
                    suppressAggFuncInHeader={true}
                    suppressContextMenu={true}
                    onCellClicked={handleCellClicked}
                    immutableData
                />
            </div>
        </div>
    );
};
